/**
 * AdvancedModeGate (SK-ADV, T-24, DS-1g-07 §7) — AC-1g-20.
 *
 * Three explicit tiers from the coordinator RBAC vocabulary. Supervisors have
 * a project-scoped PM capability seam but never receive tenant-admin content.
 * Advanced remains `tenant_admin` only (full decision list,
 * master-list power filters, Reports breadth). ONE component wraps every
 * advanced surface (single grep locus — every "advanced-only" render in
 * this codebase goes through `<AdvancedModeGate>`, never a hand-rolled
 * `role === "tenant_admin"` check elsewhere).
 *
 * Visibility-only: the real boundary is server-side scope enforcement
 * (app.py:129-139), untouched by this file. This gate mirrors that
 * boundary for rendering purposes ONLY — hiding it never grants anything
 * the server would not itself scope-check; a client bypassing this gate
 * entirely (e.g. devtools) gains nothing the server doesn't independently
 * reject. Drift is fail-closed SERVER-side by construction.
 */

import type { ComponentChildren } from "preact";

/** The provisioned client config (§7.2/§8.3): `{tenant_id, project_ids,
 * role, bearer}` installed per pilot device at onboarding. Used ONLY for
 * rendering decisions in this module — never as a security boundary. */
export interface ProvisionedClientConfig {
  tenant_id: string;
  project_ids: string[];
  role: "member" | "supervisor" | "tenant_admin";
  bearer: string;
}

export interface AdvancedModeGateProps {
  config: ProvisionedClientConfig;
  children: ComponentChildren;
  /** Rendered instead of `children` for a basic-role session. Defaults to
   * rendering nothing — "no affordance hints it" (AC-1g-20). */
  fallback?: ComponentChildren;
}

export function isAdvancedRole(config: ProvisionedClientConfig): boolean {
  return config.role === "tenant_admin";
}

/** BUI-90 mounts the PM surface at this seam. It is deliberately role-only;
 * project authority continues to come from the server-returned roster scope. */
export function isSupervisorRole(config: ProvisionedClientConfig): boolean {
  return config.role === "supervisor";
}

export function AdvancedModeGate({ config, children, fallback }: AdvancedModeGateProps) {
  if (!isAdvancedRole(config)) {
    return <>{fallback ?? null}</>;
  }
  return (
    <div data-skin-slot="SK-ADV" data-testid="advanced-mode-gate">
      {children}
    </div>
  );
}
